import React, { useEffect, useMemo, useRef, useState } from 'react';
import cs from 'classnames';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import { timeLineFormatTime } from '../../../helpers';

import { FavouriteStar } from '../../ClipFavourites/Star';
import { AngleLock } from '../../ClipFavourites/AngleLock';
import { useClipFavourites } from '../../ClipFavourites/context';
import { useClipMenu } from '../../ClipFavourites/useClipMenu';
import { useClipFilter } from '../../ClipFilter/context';
import { COMMENT, QUALIFIERS, VIDEO_TYPE, VIDEOFEED } from '../../../enums/TYPES';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import styles from './TimelineClip.module.scss';
import {
  playNextClipInOrder,
  triggerRefresh,
  updateMarkerPosition,
  updateVideoPlayedSeconds,
} from '../../../store/videos/videoSlice';
import { updateNotificationClick, updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import { findSearchValue } from '../../../helpers';

export type TClip = {
  locked_angle_id?: number | null;
  topicId?: string; // this is added in the Timeline component, hack for passing parent topic id
  qualifiers: QUALIFIERS[];
  videos?: VIDEO_TYPE[];
  isSelected?: boolean;
  comments: COMMENT[];
  time_start: number;
  original_time_start?: number | null;
  clipIndex?: number;
  topicName?: string;
  rowIndex?: number;
  time_end: number;
  original_time_end?: number | null;
  notes: { id: number; note: string }[];
  color?: string;
  width: number;
  id: string;
};

type TTimelineClip = {
  markMargin: number;
  clipIndex: number;
  rowIndex: number;
  zoom: number;
  clip: TClip;
  currentFeed: VIDEOFEED;
  clickedClipRef: React.MutableRefObject<boolean>;
  onClipSelect: (
    clip: TClip,
    topicId: string,
    rowIndex: number,
    clipIndex: number,
    topicName: string,
    doubleClick?: boolean,
    nextMenuOption?: boolean,
  ) => void;
  topicId: string;
  onDelete: () => void;
  onEdit: () => void;
  onEdgeResize?: (event: React.MouseEvent, side: 'left' | 'right') => void;
  onSend: () => void;
  topicName: string;
  clipDrawerId?: string;
  canEdit: boolean;
  isSelected: boolean;
  isGlowing?: boolean;
  suppressQualifiers?: boolean;
  dimmed: boolean;
  collapsed: boolean;
};

const QUALIFIER_MIN_GAP_PX = 20;

const ClipTrimHandles = ({ onEdgeResize }: Pick<TTimelineClip, 'onEdgeResize'>) => <>
  {(['left', 'right'] as const).map((side) => <button key={side} type="button" className={styles.trimEdge}
    data-side={side} data-preserve-clip-edit aria-label={side === 'left' ? 'Resize clip start' : 'Resize clip end'}
    tabIndex={-1} onMouseDown={(event) => {
      if (event.button !== 0 || !event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
      event.preventDefault(); event.stopPropagation(); onEdgeResize?.(event, side);
    }} onDoubleClick={(event) => {
      if (event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) event.stopPropagation();
    }} />)}
</>;

const DrawingClipMarkers = ({ clip, clipWidth }: { clip: TClip; clipWidth: number }) => {
  const { markers, showMarker } = useDrawingMarkers();
  if (clip.time_end <= clip.time_start) return null;
  return <>{markers.filter((comment) => comment.timestamp >= clip.time_start && comment.timestamp <= clip.time_end &&
    (comment.clip_id != null && String(comment.clip_id) === String(clip.id))).map((comment) => (
    <button key={comment.id} type="button" className={styles.drawingMarker}
      aria-label={`Drawing at ${timeLineFormatTime(comment.timestamp)}`} title={`Drawing · ${timeLineFormatTime(comment.timestamp)}`}
      style={{ left: Math.max(0, Math.min(clipWidth - 14, clipWidth * (comment.timestamp - clip.time_start) / (clip.time_end - clip.time_start))) }}
      onMouseDown={(event) => event.stopPropagation()} onDoubleClick={(event) => event.stopPropagation()}
      onClick={(event) => { event.stopPropagation(); showMarker(comment); }}><DrawIcon /></button>
  ))}</>;
};

const getVisibleQualifiers = (
  qualifiers: QUALIFIERS[] | undefined,
  timeStart: number,
  timeEnd: number,
  clipWidth: number,
  showAll = false,
) => {
  if (!qualifiers?.length || timeEnd <= timeStart) return [];

  if (showAll) {
    return qualifiers.reduce<Array<{ qualifier: QUALIFIERS; left: number }>>((acc, qualifier) => {
      const ratio = (qualifier.time - timeStart) / (timeEnd - timeStart);
      const left = clipWidth * ratio;

      if (left > clipWidth || left < 0) {
        return acc;
      }

      acc.push({ qualifier, left });
      return acc;
    }, []);
  }

  let lastRenderedLeft = -Infinity;

  return qualifiers.reduce<Array<{ qualifier: QUALIFIERS; left: number }>>((acc, qualifier) => {
    const ratio = (qualifier.time - timeStart) / (timeEnd - timeStart);
    const left = clipWidth * ratio;

    if (left > clipWidth || left < 0) {
      return acc;
    }

    if (left - lastRenderedLeft < QUALIFIER_MIN_GAP_PX) {
      return acc;
    }

    lastRenderedLeft = left;
    acc.push({ qualifier, left });
    return acc;
  }, []);
};

/**
 * ACTIVE TIMELINE CLIP
 * Full logic – only rendered for selected clips (like ActiveComposerClip).
 */
const ActiveTimelineClip: React.FC<TTimelineClip> = ({
  clip,
  currentFeed,
  onDelete,
  topicId,
  topicName,
  onClipSelect,
  clickedClipRef: clickedClip,
  markMargin,
  zoom,
  rowIndex,
  clipIndex,
  clipDrawerId,
  canEdit,
  onEdgeResize,
  isSelected,
  isGlowing,
  suppressQualifiers,
  dimmed,
  collapsed,
}) => {
  const [jump, setJump] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sortedSelectedClips = useAppSelector((state) => state.videoStore.sortedSelectedClips);
  const { playbackClips, enterClip } = useClipFavourites();
  const { active: clipFilterActive } = useClipFilter();
  const sidebarPlaybackActive = playbackClips !== null;
  const markerPosition = useAppSelector((state) => state.videoStore.markerPosition);
  const videoControls = useAppSelector((state) => state.videoStore.videoControls);
  const reachedEnd = useAppSelector((state) => state.videoStore.reachedEnd);
  const isDraggingCursor = useAppSelector((state) => state.sharedStore.isDraggingCursor);
  const notificationClick = useAppSelector((state) => state.sharedStore.notificationClick);
  const dispatch = useAppDispatch();
  const zoomedMargin = useMemo(() => {
    const margin = markMargin + 1;
    return margin / zoom;
  }, [zoom, markMargin]);

  const clipWidth = useMemo(() => {
    const result = clip.time_end - clip.time_start;
    return result * zoomedMargin;
  }, [clip, zoomedMargin]);

  const clipOffset = useMemo(() => {
    return `${clip.time_start * zoomedMargin + 1}px`;
  }, [clip, zoomedMargin]);

  const markerOffest = useMemo(() => {
    const result = videoControls.playedSeconds * zoomedMargin + 1;
    const sum = result - (clip.time_start * zoomedMargin + 1);
    return `${sum - 6}px`;
  }, [clip, zoomedMargin, videoControls.playedSeconds]);

  const clipStyles = useMemo(() => {
    return { width: clipWidth, left: clipOffset };
  }, [clipOffset, clipWidth]);

  const renderQualifiers = useMemo(() => {
    if (suppressQualifiers) return null;
    if (!clip.qualifiers) return null;
    return getVisibleQualifiers(clip.qualifiers, clip.time_start, clip.time_end, clipWidth, isHovered).map(({ qualifier, left }, qualifierIndex) => {
      const qualifierColor = qualifier.color ? `rgb(${qualifier.color.split(',').map((c) => +c * 256)})` : '';
      return (
        <div
          key={`qualifier-${qualifierIndex}_${qualifier.id}`}
          className={styles.qualifier}
          style={{ left, color: qualifierColor, backgroundColor: qualifierColor }}
        >
          <div className={styles.qualifierTooltip}>{qualifier.name}</div>
        </div>
      );
    });
  }, [clip.qualifiers, clip.time_start, clip.time_end, clipWidth, suppressQualifiers, isHovered]);

  const isMarkerPointsToClip = useMemo(() => {
    return markerPosition.clipIndex === clipIndex && markerPosition.rowIndex === rowIndex;
  }, [markerPosition, clipIndex, rowIndex]);

  const markerIsInClipRange = useMemo(() => {
    if (isSelected) {
      return videoControls.playedSeconds + 0.1 >= clip.time_start && videoControls.playedSeconds <= clip.time_end + 0.5;
    }
    return false;
  }, [isSelected, videoControls.playedSeconds, clip.time_start, clip.time_end]);

  useEffect(() => {
    if (clipFilterActive || sidebarPlaybackActive || !videoControls.playing || isDraggingCursor || !isMarkerPointsToClip) return;

    const isSingleClipSelected = isSelected && sortedSelectedClips.length === 1;
    const isMultipleClipsSelected = isSelected && sortedSelectedClips.length > 1;
    const isPlaybackBeyondClip = videoControls.playedSeconds > clip.time_end;

    if (isSingleClipSelected && markerIsInClipRange && isPlaybackBeyondClip) {
      if (reachedEnd) enterClip(clip.id);
      // @ts-ignore
      dispatch(playNextClipInOrder());
      return;
    }

    if (isMultipleClipsSelected) {
      if (markerIsInClipRange && isPlaybackBeyondClip) {
        // @ts-ignore
        dispatch(playNextClipInOrder());
      } else if (!markerIsInClipRange) {
        // existing commented logic left as-is
      }
      return;
    }

    if (!isSelected && sortedSelectedClips.length && !clipDrawerId) {
      // preserved original logic (though for ActiveTimelineClip isSelected is always true)
      // @ts-ignore
      dispatch(playNextClipInOrder({ ...clip, rowIndex, clipIndex }));
    }
  }, [
    clipFilterActive,
    sidebarPlaybackActive,
    enterClip,
    reachedEnd,
    sortedSelectedClips,
    markerPosition,
    videoControls.playedSeconds,
    videoControls.playing,
    markerIsInClipRange,
    isDraggingCursor,
    clipDrawerId,
    isSelected,
    clip,
    rowIndex,
    clipIndex,
    dispatch,
    isMarkerPointsToClip,
    clip.time_end,
  ]);

  const { onContextMenu, menu } = useClipMenu(
    { ...clip, topicId, clipIndex, rowIndex, topicName },
    () => {
      dispatch(updateSecondaySelectedClips([{ ...clip, topicId, clipIndex, rowIndex, topicName }]));
      onDelete();
    },
    !!clipDrawerId,
  );

  const clipColor = useMemo(() => {
    const defaultColor = '0.5,0.5,0.5';
    let colorStr = clip.color || defaultColor;
    const values = colorStr.split(',').map((c) => parseFloat(c));
    if (values.some((val) => isNaN(val))) {
      colorStr = defaultColor;
    }

    const computedColor = colorStr.split(',').map((c) => +c * 256);

    if (clip.color) {
      return sortedSelectedClips.length > 0 && isSelected
        ? {
            background: clip.color.includes('#') ? clip.color : `rgb(${computedColor})`,
            boxShadow: '0 0 3px rgba(57,179,234,0.7)',
          }
        : {
            background: `rgb(${computedColor}, 0.7)`,
          };
    } else {
      return {
        background: `rgb(${computedColor})`,
      };
    }
  }, [sortedSelectedClips, isSelected, clip, clip.color]);

  const clipRef = useRef<HTMLDivElement>(null);

  const clipToJumpQuery = findSearchValue('clipToJump');

  const scrollIntoViewIfNeeded = (el: HTMLElement, topPad = 0) => {
    if (!el) return;

    const isScrollable = (n: HTMLElement) => {
      const s = getComputedStyle(n);
      return /(auto|scroll|overlay)/.test(s.overflowY) && n.scrollHeight > n.clientHeight;
    };

    let container: HTMLElement | null = el.parentElement;
    while (container && !isScrollable(container)) container = container.parentElement;
    container = container || (document.scrollingElement as HTMLElement) || document.documentElement;

    const r = el.getBoundingClientRect();
    const c = container.getBoundingClientRect();
    const inView = r.bottom > c.top + topPad && r.top < c.bottom; // partial visibility counts

    if (!inView) {
      container.scrollTo({ top: container.scrollTop + r.top - c.top - topPad, behavior: 'auto' });
    }
  };

  useEffect(() => {
    if (clipRef.current && clipToJumpQuery?.length && +clipToJumpQuery === +clipRef.current.id && (!jump || notificationClick)) {
      const element = clipRef.current;

      element.style.scrollMarginTop = '60px';
      scrollIntoViewIfNeeded(element, 60);
      element.style.scrollMarginTop = '';
      dispatch(updateNotificationClick(false));
      setJump(true);
    }
  }, [isMarkerPointsToClip, markerIsInClipRange, notificationClick, jump, clipToJumpQuery, dispatch]);

  useEffect(() => {
    if (isMarkerPointsToClip && clipRef?.current) {
      const element = clipRef.current;

      element.style.scrollMarginTop = '60px';
      scrollIntoViewIfNeeded(element, 60);
      element.style.scrollMarginTop = '';
    }
  }, [isMarkerPointsToClip]);

  return (
    <div
      key={clip.id}
      onContextMenu={onContextMenu}
      onMouseDown={(e) => {
        if (e.button === 0) {
          clickedClip.current = true;
          onClipSelect(clip, topicId, rowIndex, clipIndex, topicName, false, true);
          setTimeout(() => {
            clickedClip.current = false;
          }, 500);
        }
      }}
      onDoubleClick={(e) => {
        if (e.button === 0) {
          clickedClip.current = true;
          onClipSelect(clip, topicId, rowIndex, clipIndex, topicName, true, true);
          setTimeout(() => {
            clickedClip.current = false;
          }, 500);
        }
      }}
    >
      <div className={isSelected ? styles.selectedBorder : ''}>
        <div
          ref={clipRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={
            isSelected
              ? { width: `${clipStyles.width}px`, left: clipStyles.left }
              : { width: `${clipStyles.width}px`, left: clipStyles.left, ...clipColor }
          }
          className={cs(
            isSelected ? styles.selectedTimelineClip : styles.timelineClip,
            isSelected ? styles.selected : '',
            isMarkerPointsToClip && isSelected && styles.activeSelected,
            isGlowing && styles.glowClip,
            dimmed && styles.dimmed,
            collapsed && styles.collapsed,
          )}
          id={clip.id}
        >
          {isSelected && (
            <div style={clipColor ? { background: (clipColor as any).background } : {}} className={styles.relativeContainer}>
              <div className={styles.topDot} />
              {isMarkerPointsToClip && markerIsInClipRange && <div style={{ left: markerOffest }} className={styles.timelineClipMarker} />}
              <div className={styles.bottomDot} />
            </div>
          )}
          <FavouriteStar clipId={clip.id} badge clipWidth={clipWidth} angleLocked={clip.locked_angle_id != null} />
          <AngleLock clip={clip} videos={currentFeed.videos} clipWidth={clipWidth} />
          {renderQualifiers}
          <DrawingClipMarkers clip={clip} clipWidth={clipWidth} />
          {canEdit && !clipDrawerId && onEdgeResize && <ClipTrimHandles onEdgeResize={onEdgeResize} />}
          {menu}
        </div>
      </div>
    </div>
  );
};

/**
 * DUMB TIMELINE CLIP
 * Lightweight, no marker/autoplay/scroll logic.
 * Shares clip actions without changing the current selection.
 */
const DumbTimelineClipInner: React.FC<TTimelineClip> = ({
  clip,
  currentFeed,
  onDelete,
  topicId,
  topicName,
  onClipSelect,
  clickedClipRef: clickedClip,
  markMargin,
  zoom,
  rowIndex,
  clipIndex,
  clipDrawerId,
  canEdit,
  onEdgeResize,
  isGlowing,
  dimmed,
  collapsed,
}) => {
  const { time_start, time_end, qualifiers, color } = clip;
  const [isHovered, setIsHovered] = useState(false);

  const zoomedMargin = useMemo(() => {
    const margin = markMargin + 1;
    return margin / zoom;
  }, [zoom, markMargin]);

  const clipWidth = useMemo(() => {
    const result = time_end - time_start;
    return result * zoomedMargin;
  }, [time_end, time_start, zoomedMargin]);

  const clipOffset = useMemo(() => {
    return `${time_start * zoomedMargin + 1}px`;
  }, [time_start, zoomedMargin]);

  const renderQualifiers = useMemo(() => {
    if (!qualifiers || !qualifiers.length) return null;

    return getVisibleQualifiers(qualifiers, time_start, time_end, clipWidth, isHovered).map(({ qualifier, left }, qualifierIndex) => {
      const qualifierColor = qualifier.color ? `rgb(${qualifier.color.split(',').map((c) => +c * 256)})` : '';

      return (
        <div
          key={`qualifier-${qualifierIndex}_${qualifier.id}`}
          className={styles.qualifier}
          style={{ left, color: qualifierColor, backgroundColor: qualifierColor }}
        >
          <div className={styles.qualifierTooltip}>{qualifier.name}</div>
        </div>
      );
    });
  }, [qualifiers, time_start, time_end, clipWidth, isHovered]);

  const clipColor = useMemo(() => {
    const defaultColor = '0.5,0.5,0.5';
    let colorStr = color || defaultColor;
    const values = colorStr.split(',').map((c) => parseFloat(c));
    if (values.some((val) => isNaN(val))) {
      colorStr = defaultColor;
    }

    const computedColor = colorStr.split(',').map((c) => +c * 256);

    return color ? { background: color.includes('#') ? color : `rgb(${computedColor}, 0.7)` } : { background: `rgb(${computedColor})` };
  }, [color]);

  const dispatch = useAppDispatch();
  const { onContextMenu, menu } = useClipMenu(
    { ...clip, topicId, clipIndex, rowIndex, topicName },
    () => {
      dispatch(updateSecondaySelectedClips([{ ...clip, topicId, clipIndex, rowIndex, topicName }]));
      onDelete();
    },
    !!clipDrawerId,
  );

  return (
    <div
      key={clip.id}
      onContextMenu={onContextMenu}
      onMouseDown={(e) => {
        if (e.button === 0) {
          clickedClip.current = true;
          onClipSelect(clip, topicId, rowIndex, clipIndex, topicName, false, true);
          setTimeout(() => {
            clickedClip.current = false;
          }, 500);
        }
      }}
      onDoubleClick={(e) => {
        if (e.button === 0) {
          clickedClip.current = true;
          onClipSelect(clip, topicId, rowIndex, clipIndex, topicName, true, true);
          setTimeout(() => {
            clickedClip.current = false;
          }, 500);
        }
      }}
    >
      <div>
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ width: clipWidth, left: clipOffset, ...clipColor }}
          className={cs(styles.timelineClip, isGlowing && styles.glowClip, dimmed && styles.dimmed, collapsed && styles.collapsed)}
          id={clip.id}
        >
          <FavouriteStar clipId={clip.id} badge clipWidth={clipWidth} angleLocked={clip.locked_angle_id != null} />
          <AngleLock clip={clip} videos={currentFeed.videos} clipWidth={clipWidth} />
          {renderQualifiers}
          <DrawingClipMarkers clip={clip} clipWidth={clipWidth} />
          {canEdit && !clipDrawerId && onEdgeResize && <ClipTrimHandles onEdgeResize={onEdgeResize} />}
          {menu}
        </div>
      </div>
    </div>
  );
};

const DumbTimelineClip = React.memo(DumbTimelineClipInner) as React.FC<TTimelineClip>;

/**
 * WRAPPER – decides between Active / Dumb (like ComposerClip).
 * Now selection is computed in parent and passed in.
 */
export const TimelineClip: React.FC<TTimelineClip> = (props) => {
  const isSelected = props.isSelected ?? false;

  return isSelected ? <ActiveTimelineClip {...props} isSelected /> : <DumbTimelineClip {...props} isSelected={false} />;
};
